import React, { useState } from "react";
import { X, AlertCircle, CheckCircle, Clock, FileText, User, Download, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useIssuedInvoiceDetail } from "../../api/hooks/useIssuedInvoices";
import { useEnqueueInvoiceImport } from "../../api/hooks/useAsyncInvoiceImport";
import { formatDate, formatDateTime, formatCurrency } from "../../utils/formatters";
import { IssuedInvoiceSourceQuery } from "../../api/generated/api-client";

interface IssuedInvoiceDetailModalProps {
  invoiceId: string;
  isOpen: boolean;
  onClose: () => void;
  onInvoiceUpdated?: () => Promise<void>;
  onJobStarted?: (jobId: string) => void;
}

const IssuedInvoiceDetailModal: React.FC<IssuedInvoiceDetailModalProps> = ({
  invoiceId,
  isOpen,
  onClose,
  onInvoiceUpdated,
  onJobStarted,
}) => {
  const { data, isLoading, error } = useIssuedInvoiceDetail(invoiceId);
  const [reimporting, setReimporting] = useState(false);
  const [expandedSyncItems, setExpandedSyncItems] = useState<Set<string>>(new Set());
  const enqueueImportMutation = useEnqueueInvoiceImport();

  if (!isOpen) return null;

  const handleReimport = async (currency?: 'CZK' | 'EUR') => {
    // Use invoice's existing currency as default if not specified
    const targetCurrency = currency || (data?.invoice as any)?.currency || 'CZK';
    if (reimporting) return;

    try {
      setReimporting(true);

      const requestBody = {
        query: new IssuedInvoiceSourceQuery({
          requestId: `reimport-${invoiceId}-${Date.now()}`,
          invoiceId: invoiceId,
          currency: targetCurrency,
        }),
      };

      // Use async import
      const result = await enqueueImportMutation.mutateAsync(requestBody);

      // Pass jobId to parent and close modal
      if (result.jobId && onJobStarted) {
        onJobStarted(result.jobId);
      }
      
      // Close modal immediately
      onClose();

      // Refresh data in background
      if (onInvoiceUpdated) {
        onInvoiceUpdated();
      }

    } catch (error) {
      console.error('Reimport error:', error);
    } finally {
      setReimporting(false);
    }
  };

  const getSyncStatusBadge = (isSynced: boolean, errorType?: string | null) => {
    if (errorType) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          Chyba při synchronizaci
        </span>
      );
    }
    if (isSynced) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-emerald-900/30 dark:text-emerald-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          Synchronizováno
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-amber-900/30 dark:text-amber-300">
        <Clock className="h-3 w-3 mr-1" />
        Čeká na synchronizaci
      </span>
    );
  };

  const getSyncStatusIcon = (isSuccess: boolean) => {
    return isSuccess 
      ? <CheckCircle className="h-4 w-4 text-green-500 dark:text-emerald-400" />
      : <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400" />;
  };

  const toggleSyncItemExpansion = (syncId: string) => {
    setExpandedSyncItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(syncId)) {
        newSet.delete(syncId);
      } else {
        newSet.add(syncId);
      }
      return newSet;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden dark:bg-graphite-surface dark:shadow-soft-dark">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-graphite-border">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-gray-400 mr-3 dark:text-graphite-faint" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-graphite-text">
              Detail faktury {invoiceId}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 dark:text-graphite-faint dark:hover:text-graphite-muted"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Loading state */}
          {isLoading && (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-4 dark:text-graphite-muted">Načítání detailu faktury...</p>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="p-6 text-center">
              <div className="text-red-600 dark:text-red-400">
                <AlertCircle className="h-12 w-12 mx-auto mb-4" />
                <p className="font-medium">Chyba při načítání detailu faktury</p>
                <p className="text-sm mt-1">{error.message}</p>
              </div>
            </div>
          )}

          {/* Invoice detail */}
          {data && data.invoice && (
            <div className="p-6">
              {/* Two-column layout: Basic info left, Import history right */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 h-full">
                
                {/* Left column - Basic information */}
                <div className="space-y-6">
                  {/* Basic information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 dark:text-graphite-text dark:border-graphite-border">
                      Základní informace
                    </h3>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-graphite-muted">Číslo faktury</label>
                        <p className="text-sm text-gray-900 dark:text-graphite-text">{data.invoice.id}</p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-graphite-muted">Datum faktury</label>
                        <p className="text-sm text-gray-900 dark:text-graphite-text">{formatDate(data.invoice.invoiceDate)}</p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-graphite-muted">Celková částka</label>
                        <p className="text-sm text-gray-900 font-semibold dark:text-graphite-text">
                          {formatCurrency(data.invoice.price ?? null)}
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                            (data.invoice as any).currency === 'EUR'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            {(data.invoice as any).currency || 'CZK'}
                          </span>
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-graphite-muted">Stav synchronizace</label>
                        <div className="mt-1">
                          {getSyncStatusBadge(data.invoice.isSynced ?? false, data.invoice.errorType)}
                        </div>
                      </div>
                      
                      {data.invoice.lastSyncTime && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 dark:text-graphite-muted">Poslední synchronizace</label>
                          <p className="text-sm text-gray-900 dark:text-graphite-text">{formatDateTime(data.invoice.lastSyncTime)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 dark:text-graphite-text dark:border-graphite-border">
                      Informace o zákazníkovi
                    </h3>
                    
                    <div className="space-y-3">
                      {data.invoice.customerName && (
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0 dark:text-graphite-faint" />
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-graphite-muted">Jméno</label>
                            <p className="text-sm text-gray-900 dark:text-graphite-text">{data.invoice.customerName}</p>
                          </div>
                        </div>
                      )}
                      
                    </div>
                  </div>

{/* Error details */}
                  {data.invoice.errorType && data.invoice.errorMessage && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30 dark:border-red-900/40">
                      <h3 className="text-lg font-medium text-red-800 mb-2 dark:text-red-300">
                        Chyba synchronizace
                      </h3>
                      <p className="text-sm text-red-600 dark:text-red-400">{data.invoice.errorMessage}</p>
                    </div>
                  )}
                </div>

                {/* Right column - Import history */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 dark:text-graphite-text dark:border-graphite-border">
                    Historie importu 
                    {data.invoice.syncHistory && data.invoice.syncHistory.length > 0 && (
                      <span className="text-sm font-normal text-gray-500 ml-2 dark:text-graphite-muted">
                        ({data.invoice.syncHistory.length} pokusů)
                      </span>
                    )}
                  </h3>
                  
                  {data.invoice.syncHistory && data.invoice.syncHistory.length > 0 ? (
                    <div className="space-y-3 max-h-[calc(90vh-300px)] overflow-y-auto">
                      {[...data.invoice.syncHistory]
                        .sort((a, b) => {
                          const tb = b.syncTime ? new Date(b.syncTime).getTime() : 0;
                          const ta = a.syncTime ? new Date(a.syncTime).getTime() : 0;
                          return tb - ta;
                        })
                        .map((sync, index) => {
                          const isExpanded = expandedSyncItems.has((sync.id ?? '').toString());
                          const isLatest = index === 0;
                          
                          return (
                            <div 
                              key={sync.id} 
                              className={`border rounded-lg ${
                                sync.isSuccess
                                  ? isLatest ? 'border-green-300 bg-green-50 dark:border-emerald-900/40 dark:bg-emerald-900/30' : 'border-green-200 bg-green-25 dark:border-emerald-900/40 dark:bg-emerald-900/20'
                                  : isLatest ? 'border-red-300 bg-red-50 dark:border-red-900/40 dark:bg-red-900/30' : 'border-red-200 bg-red-25 dark:border-red-900/40 dark:bg-red-900/20'
                              }`}
                            >
                              <div 
                                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors dark:hover:bg-white/5"
                                onClick={() => toggleSyncItemExpansion((sync.id ?? '').toString())}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {getSyncStatusIcon(sync.isSuccess ?? false)}
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className={`font-medium ${
                                          sync.isSuccess ? 'text-green-800 dark:text-emerald-300' : 'text-red-800 dark:text-red-300'
                                        }`}>
                                          {sync.isSuccess ? 'Import úspěšný' : 'Import neúspěšný'}
                                        </span>
                                        {isLatest && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                            Nejnovější
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-500 dark:text-graphite-muted">
                                        {formatDateTime(sync.syncTime)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {sync.error && (
                                      <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded dark:text-red-300 dark:bg-red-900/30">
                                        Chyba
                                      </span>
                                    )}
                                    {isExpanded ? (
                                      <ChevronUp className="h-5 w-5 text-gray-400 dark:text-graphite-faint" />
                                    ) : (
                                      <ChevronDown className="h-5 w-5 text-gray-400 dark:text-graphite-faint" />
                                    )}
                                  </div>
                                </div>
                                
                                {/* Quick error preview when collapsed */}
                                {!isExpanded && sync.error && (
                                  <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
                                    <strong>Chyba:</strong> {sync.error.message}
                                  </div>
                                )}
                              </div>
                              
                              {/* Expandable content */}
                              {isExpanded && (
                                <div className="border-t border-gray-200 p-4 space-y-3 dark:border-graphite-border">
                                  {/* Error details */}
                                  {sync.error && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/30 dark:border-red-900/40">
                                      <h4 className="font-medium text-red-800 mb-2 dark:text-red-300">
                                        Detail chyby pro tento pokus
                                      </h4>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium text-red-700 dark:text-red-300">Zpráva:</span>
                                          <p className="text-red-600 mt-1 dark:text-red-400">{sync.error.message}</p>
                                        </div>
<div>
                                          <span className="font-medium text-red-700 dark:text-red-300">Typ chyby:</span>
                                          <p className="text-red-600 text-xs dark:text-red-400">
                                            {sync.error.errorType === 'General' ? 'Obecná chyba' :
                                             sync.error.errorType === 'InvoicePaired' ? 'Faktura již spárována' :
                                             sync.error.errorType === 'ProductNotFound' ? 'Produkt nenalezen' :
                                             sync.error.errorType ? `Neznámý typ (${sync.error.errorType})` : 'Neznámý typ'}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Current error state (if this is the latest attempt and there's a current error) */}
                                  {isLatest && data?.invoice?.errorType && data?.invoice?.errorMessage && (
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 dark:bg-orange-900/30 dark:border-orange-900/40">
                                      <h4 className="font-medium text-orange-800 mb-2 dark:text-orange-300">
                                        Aktuální stav synchronizace
                                      </h4>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium text-orange-700 dark:text-orange-300">Zpráva:</span>
                                          <p className="text-orange-600 mt-1 dark:text-orange-400">{data?.invoice?.errorMessage}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium text-orange-700 dark:text-orange-300">Typ chyby:</span>
                                          <p className="text-orange-600 text-xs dark:text-orange-400">
                                            {data?.invoice?.errorType === 'General' ? 'Obecná chyba' :
                                             data?.invoice?.errorType === 'InvoicePaired' ? 'Faktura již spárována' :
                                             data?.invoice?.errorType === 'ProductNotFound' ? 'Produkt nenalezen' :
                                             data?.invoice?.errorType ? `Neznámý typ (${data?.invoice?.errorType})` : 'Neznámý typ'}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Raw data from the import attempt */}
                                  {sync.data && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 dark:bg-graphite-surface-2 dark:border-graphite-border">
                                      <details className="text-sm">
                                        <summary className="font-medium text-gray-700 cursor-pointer hover:text-gray-900 mb-2 dark:text-graphite-muted dark:hover:text-graphite-text">
                                          Zobrazit raw data z tohoto pokusu
                                        </summary>
                                        <pre className="mt-2 p-2 bg-white border rounded text-xs overflow-x-auto whitespace-pre-wrap font-mono text-gray-600 max-h-40 dark:bg-graphite-surface dark:border-graphite-border dark:text-graphite-muted">
                                          {(() => { try { return JSON.stringify(JSON.parse(sync.data), null, 2); } catch { return sync.data; } })()}
                                        </pre>
                                      </details>
                                    </div>
                                  )}

                                  {/* Synchronization result from external system */}
                                  {sync.adapterResponse && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30 dark:border-blue-900/40">
                                      <details className="text-sm">
                                        <summary className="font-medium text-blue-700 cursor-pointer hover:text-blue-900 mb-2 dark:text-blue-300 dark:hover:text-blue-200">
                                          Detail odpovědi ze systému ABRA Flexi
                                        </summary>
                                        <div className="mt-2 space-y-2">
                                          {(() => {
                                            try {
                                              const parsed = JSON.parse(sync.adapterResponse!);
                                              const winstrom = parsed?.winstrom;
                                              const invoice = winstrom?.['vydana-faktura']?.[0];
                                              const errors = winstrom?.errors;
                                              if (errors && Array.isArray(errors) && errors.length > 0) {
                                                return (
                                                  <div className="p-2 bg-white border border-red-200 rounded text-xs space-y-1 dark:bg-graphite-surface dark:border-red-900/40">
                                                    {errors.map((e: { message?: string }, i: number) => (
                                                      <div key={i} className="text-red-700 dark:text-red-300">{e.message ?? JSON.stringify(e)}</div>
                                                    ))}
                                                  </div>
                                                );
                                              }
                                              if (invoice) {
                                                return (
                                                  <div className="p-2 bg-white border rounded text-xs dark:bg-graphite-surface dark:border-graphite-border">
                                                    <div className="space-y-1">
                                                      {invoice.id && (
                                                        <div>
                                                          <span className="font-medium text-blue-700 dark:text-blue-300">ID faktury:</span>
                                                          <span className="ml-2 font-mono">{invoice.id}</span>
                                                        </div>
                                                      )}
                                                      {invoice.kod && (
                                                        <div>
                                                          <span className="font-medium text-blue-700 dark:text-blue-300">Kód faktury:</span>
                                                          <span className="ml-2 font-mono">{invoice.kod}</span>
                                                        </div>
                                                      )}
                                                      {invoice.datVyst && (
                                                        <div>
                                                          <span className="font-medium text-blue-700 dark:text-blue-300">Datum vystavení:</span>
                                                          <span className="ml-2">{invoice.datVyst}</span>
                                                        </div>
                                                      )}
                                                      {invoice.mena && (
                                                        <div>
                                                          <span className="font-medium text-blue-700 dark:text-blue-300">Měna:</span>
                                                          <span className="ml-2">{invoice.mena}</span>
                                                        </div>
                                                      )}
                                                      {invoice.nazFirmy && (
                                                        <div>
                                                          <span className="font-medium text-blue-700 dark:text-blue-300">Zákazník:</span>
                                                          <span className="ml-2">{invoice.nazFirmy}</span>
                                                        </div>
                                                      )}
                                                      {invoice.polozkyDokladu && Array.isArray(invoice.polozkyDokladu) && (
                                                        <div>
                                                          <span className="font-medium text-blue-700 dark:text-blue-300">Počet položek:</span>
                                                          <span className="ml-2">{invoice.polozkyDokladu.length}</span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              }
                                              return (
                                                <pre className="p-2 bg-white border rounded text-xs overflow-x-auto whitespace-pre-wrap font-mono text-gray-600 dark:bg-graphite-surface dark:border-graphite-border dark:text-graphite-muted">
                                                  {JSON.stringify(parsed, null, 2)}
                                                </pre>
                                              );
                                            } catch {
                                              return (
                                                <p className="text-gray-600 text-xs p-2 bg-white border rounded dark:text-graphite-muted dark:bg-graphite-surface dark:border-graphite-border">
                                                  Nepodařilo se parsovat data jako JSON
                                                </p>
                                              );
                                            }
                                          })()}
                                        </div>
                                      </details>
                                    </div>
                                  )}
                                  
                                  {/* Success state */}
                                  {sync.isSuccess && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 dark:bg-emerald-900/30 dark:border-emerald-900/40">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-emerald-400" />
                                        <span className="font-medium text-green-800 dark:text-emerald-300">
                                          Import byl dokončen úspěšně
                                        </span>
                                      </div>
                                      <p className="text-sm text-green-600 mt-1 dark:text-emerald-400">
                                        Faktura byla úspěšně importována do systému ABRA Flexi.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-graphite-muted">
                      <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-graphite-faint" />
                      <p className="text-sm">Zatím žádná historie importu</p>
                      <p className="text-xs mt-1">Faktura ještě nebyla importována</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 dark:border-graphite-border dark:bg-graphite-surface-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {data && data.invoice && (
                <button
                  onClick={() => handleReimport()}
                  disabled={reimporting}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 flex items-center gap-2"
                >
                  {reimporting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Importuje...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Znovu importovat
                    </>
                  )}
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:text-graphite-muted dark:bg-graphite-surface-2 dark:border-graphite-border dark:hover:bg-white/5"
            >
              Zavřít
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuedInvoiceDetailModal;